import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFilter, faChevronDown, faDownload, faPlus } from '@fortawesome/free-solid-svg-icons';
import ClientService from '../../../../AppSettings/services/ClientService';
import EmployeeService from '../../../../AppSettings/services/EmployeeService';
import type { ScheduleAggregateFilters, ScheduleProjectSummary } from '../../../../types/workflows';
import './ScheduleFilters.css';

/**
 * Schedule Filters Component
 * 
 * Provides filtering capabilities for the Schedule/Gantt view:
 * - Multi-select dropdowns for Discipline, Status, Client, Project Type, Owner
 * - Date range pickers for Start Date and End Date
 * - Search input (debounced)
 * - Filter chips showing active filters with remove buttons
 * - Clear all button
 */

interface ScheduleFiltersProps {
  filters: ScheduleAggregateFilters;
  onFiltersChange: (filters: ScheduleAggregateFilters) => void;
  projects?: ScheduleProjectSummary[];
  searchInput: string;
  onSearchChange: (value: string) => void;
  onExportClick?: () => void;
  viewMode?: 'table' | 'timeline';
  onViewModeChange?: (mode: 'table' | 'timeline') => void;
  zoomLevel?: 'week' | 'month' | 'quarter';
  onZoomLevelChange?: (level: 'week' | 'month' | 'quarter') => void;
}

interface SelectOption {
  value: string;
  label: string;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ScheduleFilters: React.FC<ScheduleFiltersProps> = ({
  filters,
  onFiltersChange,
  projects = [],
  searchInput,
  onSearchChange,
  onExportClick,
  viewMode,
  onViewModeChange,
  zoomLevel,
  onZoomLevelChange,
}) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Debounce search input
  const debouncedSearch = useDebounce(searchInput, 500);

  // Update filters when debounced search changes
  useEffect(() => {
    // Search will be handled separately, not as part of ScheduleAggregateFilters
    // This is just for UI consistency
  }, [debouncedSearch]);

  // Fetch clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => ClientService.getClients(),
    staleTime: 300000, // 5 minutes
  });

  // Fetch employees for dropdown
  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => EmployeeService.getEmployees(),
    staleTime: 300000, // 5 minutes
  });

  // Extract unique values from projects for filter options
  const filterOptions = useMemo(() => {
    const disciplines = new Set<string>();
    const statuses = new Set<string>();
    const projectTypes = new Set<string>();

    projects.forEach(project => {
      if (project.disciplines) {
        project.disciplines.forEach(d => disciplines.add(d));
      }
      if (project.projectStatus) {
        statuses.add(project.projectStatus);
      }
      if (project.projectType) {
        projectTypes.add(project.projectType);
      }
    });

    return {
      disciplines: Array.from(disciplines).sort(),
      statuses: Array.from(statuses).sort(),
      projectTypes: Array.from(projectTypes).sort(),
    };
  }, [projects]);

  // Prepare client options
  const clientOptions: SelectOption[] = useMemo(() => {
    if (!clientsData || !Array.isArray(clientsData)) return [];
    return clientsData.map(client => {
      const clientId = (client as any).ClientID || (client as any).clientid || '';
      const firstName = (client as any).FirstName || (client as any).firstname || '';
      const lastName = (client as any).LastName || (client as any).lastname || '';
      const company = (client as any).Company || (client as any).company || '';
      const label = `${firstName} ${lastName} ${company}`.trim() || clientId || 'Unknown';
      return { value: clientId, label };
    });
  }, [clientsData]);

  // Prepare employee options
  const employeeOptions: SelectOption[] = useMemo(() => {
    if (!employeesData || !Array.isArray(employeesData)) return [];
    return employeesData.map(emp => {
      const empId = (emp as any).EmployeeID || (emp as any).employeeid || '';
      const firstName = (emp as any).FirstName || (emp as any).firstname || '';
      const lastName = (emp as any).LastName || (emp as any).lastname || '';
      const label = `${firstName} ${lastName}`.trim() || empId || 'Unknown';
      return { value: empId, label };
    });
  }, [employeesData]);

  // Toggle filter value
  const toggleFilter = useCallback((type: keyof ScheduleAggregateFilters, value: string) => {
    const current = filters[type];
    
    if (type === 'startDate' || type === 'endDate') {
      // Date fields are single values, not arrays
      onFiltersChange({
        ...filters,
        [type]: current === value ? null : value,
      });
      return;
    }

    // Array fields
    const array = Array.isArray(current) ? current : [];
    const newArray = array.includes(value)
      ? array.filter(v => v !== value)
      : [...array, value];
    
    onFiltersChange({
      ...filters,
      [type]: newArray.length > 0 ? newArray : undefined,
    });
  }, [filters, onFiltersChange]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    onFiltersChange({
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      // Keep date range, clear everything else
    });
    onSearchChange('');
  }, [filters, onFiltersChange, onSearchChange]);

  // Remove individual filter chip
  const removeFilter = useCallback((type: keyof ScheduleAggregateFilters, value: string) => {
    toggleFilter(type, value);
  }, [toggleFilter]);

  // Check if any filters are active (excluding date range)
  const hasActiveFilters = useMemo(() => {
    return !!(
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.disciplines && filters.disciplines.length > 0) ||
      (filters.clientIds && filters.clientIds.length > 0) ||
      (filters.projectTypes && filters.projectTypes.length > 0) ||
      (filters.userIds && filters.userIds.length > 0) ||
      searchInput.trim()
    );
  }, [filters, searchInput]);

  // Toggle dropdown
  const toggleDropdown = useCallback((dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  }, [openDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="schedule-filters">
      {/* Filter Toggle Button */}
      <div className="filter-header">
        <div className="filter-header-left">
          <button
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
          >
            <FontAwesomeIcon icon={faFilter} />
            <span>Filters</span>
            {hasActiveFilters && <span className="filter-badge">{hasActiveFilters ? '•' : ''}</span>}
          </button>
          {onViewModeChange && viewMode !== undefined && (
            <div className="schedule-filters-view-toggle">
              <button
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => onViewModeChange('table')}
                aria-label="Table view"
              >
                Table View
              </button>
              <button
                className={`view-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                onClick={() => onViewModeChange('timeline')}
                aria-label="Timeline view"
              >
                Timeline View
              </button>
            </div>
          )}
        </div>
        {hasActiveFilters && (
          <button className="btn-link clear-all-btn" onClick={clearAllFilters}>
            Clear All
          </button>
        )}
        <div className="schedule-filters-actions">
          <button
            type="button"
            className="btn btn-secondary create-template-btn"
            onClick={() => navigate('/templates')}
            aria-label="Create new workflow template"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create New
          </button>
          {onExportClick && (
            <button
              className="btn btn-primary export-btn"
              onClick={onExportClick}
              aria-label="Export schedule"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export
            </button>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="filter-panel">
          {/* Search Input */}
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search projects..."
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {/* Time range / Zoom: Week, Month, Quarter */}
          {onZoomLevelChange && zoomLevel !== undefined && (
            <div className="filter-group filter-group-zoom">
              <label className="filter-label">Time range</label>
              <div className="zoom-controls">
                <button
                  className={`zoom-btn ${zoomLevel === 'week' ? 'active' : ''}`}
                  onClick={() => onZoomLevelChange('week')}
                  aria-label="Week view"
                >
                  Week
                </button>
                <button
                  className={`zoom-btn ${zoomLevel === 'month' ? 'active' : ''}`}
                  onClick={() => onZoomLevelChange('month')}
                  aria-label="Month view"
                >
                  Month
                </button>
                <button
                  className={`zoom-btn ${zoomLevel === 'quarter' ? 'active' : ''}`}
                  onClick={() => onZoomLevelChange('quarter')}
                  aria-label="Quarter view"
                >
                  Quarter
                </button>
              </div>
            </div>
          )}

          {/* Multi-Select Filters */}
          <div className="filter-row">
            {/* Discipline Multi-Select */}
            <div className="filter-group filter-dropdown">
              <label className="filter-label">Discipline</label>
              <div className="multi-select-wrapper">
                <button
                  className="multi-select-trigger"
                  onClick={() => toggleDropdown('discipline')}
                >
                  <span>
                    {filters.disciplines && filters.disciplines.length > 0
                      ? `${filters.disciplines.length} selected`
                      : 'All Disciplines'}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {openDropdown === 'discipline' && (
                  <div className="multi-select-menu">
                    {filterOptions.disciplines.map(discipline => (
                      <label key={discipline} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.disciplines?.includes(discipline) || false}
                          onChange={() => toggleFilter('disciplines', discipline)}
                        />
                        <span>{discipline}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Status Multi-Select */}
            <div className="filter-group filter-dropdown">
              <label className="filter-label">Status</label>
              <div className="multi-select-wrapper">
                <button
                  className="multi-select-trigger"
                  onClick={() => toggleDropdown('status')}
                >
                  <span>
                    {filters.statuses && filters.statuses.length > 0
                      ? `${filters.statuses.length} selected`
                      : 'All Statuses'}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {openDropdown === 'status' && (
                  <div className="multi-select-menu">
                    {filterOptions.statuses.map(status => (
                      <label key={status} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.statuses?.includes(status) || false}
                          onChange={() => toggleFilter('statuses', status)}
                        />
                        <span>{status}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Client Multi-Select */}
            <div className="filter-group filter-dropdown">
              <label className="filter-label">Client</label>
              <div className="multi-select-wrapper">
                <button
                  className="multi-select-trigger"
                  onClick={() => toggleDropdown('client')}
                >
                  <span>
                    {filters.clientIds && filters.clientIds.length > 0
                      ? `${filters.clientIds.length} selected`
                      : 'All Clients'}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {openDropdown === 'client' && (
                  <div className="multi-select-menu">
                    {clientOptions.map(client => (
                      <label key={client.value} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.clientIds?.includes(client.value) || false}
                          onChange={() => toggleFilter('clientIds', client.value)}
                        />
                        <span>{client.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Project Type Multi-Select */}
            <div className="filter-group filter-dropdown">
              <label className="filter-label">Project Type</label>
              <div className="multi-select-wrapper">
                <button
                  className="multi-select-trigger"
                  onClick={() => toggleDropdown('projectType')}
                >
                  <span>
                    {filters.projectTypes && filters.projectTypes.length > 0
                      ? `${filters.projectTypes.length} selected`
                      : 'All Types'}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {openDropdown === 'projectType' && (
                  <div className="multi-select-menu">
                    {filterOptions.projectTypes.map(type => (
                      <label key={type} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.projectTypes?.includes(type) || false}
                          onChange={() => toggleFilter('projectTypes', type)}
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Owner Multi-Select */}
            <div className="filter-group filter-dropdown">
              <label className="filter-label">Owner</label>
              <div className="multi-select-wrapper">
                <button
                  className="multi-select-trigger"
                  onClick={() => toggleDropdown('owner')}
                >
                  <span>
                    {filters.userIds && filters.userIds.length > 0
                      ? `${filters.userIds.length} selected`
                      : 'All Owners'}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {openDropdown === 'owner' && (
                  <div className="multi-select-menu">
                    {employeeOptions.map(emp => (
                      <label key={emp.value} className="multi-select-option">
                        <input
                          type="checkbox"
                          checked={filters.userIds?.includes(emp.value) || false}
                          onChange={() => toggleFilter('userIds', emp.value)}
                        />
                        <span>{emp.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Date Range Pickers */}
          <div className="filter-row">
            <div className="filter-group">
              <label className="filter-label">Start Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.startDate || ''}
                onChange={(e) => onFiltersChange({ ...filters, startDate: e.target.value || null })}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">End Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.endDate || ''}
                onChange={(e) => onFiltersChange({ ...filters, endDate: e.target.value || null })}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filter Chips */}
      {hasActiveFilters && (
        <div className="filter-chips">
          {filters.statuses?.map(status => (
            <span key={`status-${status}`} className="filter-chip">
              Status: {status}
              <button
                className="chip-remove"
                onClick={() => removeFilter('statuses', status)}
                aria-label={`Remove status filter ${status}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
          {filters.disciplines?.map(discipline => (
            <span key={`discipline-${discipline}`} className="filter-chip">
              Discipline: {discipline}
              <button
                className="chip-remove"
                onClick={() => removeFilter('disciplines', discipline)}
                aria-label={`Remove discipline filter ${discipline}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
          {filters.clientIds?.map(clientId => {
            const client = clientOptions.find(c => c.value === clientId);
            return (
              <span key={`client-${clientId}`} className="filter-chip">
                Client: {client?.label || clientId}
                <button
                  className="chip-remove"
                  onClick={() => removeFilter('clientIds', clientId)}
                  aria-label={`Remove client filter ${clientId}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </span>
            );
          })}
          {filters.projectTypes?.map(type => (
            <span key={`type-${type}`} className="filter-chip">
              Type: {type}
              <button
                className="chip-remove"
                onClick={() => removeFilter('projectTypes', type)}
                aria-label={`Remove project type filter ${type}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
          {filters.userIds?.map(userId => {
            const emp = employeeOptions.find(e => e.value === userId);
            return (
              <span key={`owner-${userId}`} className="filter-chip">
                Owner: {emp?.label || userId}
                <button
                  className="chip-remove"
                  onClick={() => removeFilter('userIds', userId)}
                  aria-label={`Remove owner filter ${userId}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </span>
            );
          })}
          {searchInput.trim() && (
            <span className="filter-chip">
              Search: {searchInput}
              <button
                className="chip-remove"
                onClick={() => onSearchChange('')}
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleFilters;

